import type { PropsWithChildren, FunctionComponent } from 'react'
import { useEffect } from 'react'
import { useTheme } from '@emotion/react'
import FeatureEducationModal from '../../legacy/components/FeatureHighlights/FeatureEducationModal'
import useFeatureEducation from '../../legacy/components/FeatureHighlights/useFeatureEducation'
import { AppContentContainer } from '../../gin-and-tonic/containers/app-content-container/AppContentContainer'
import ErrorBoundary from '../../legacy/components/ErrorBoundary'
import { type DashRouteComponentProps } from '../../legacy/dash-components/dash-route/utils/dashRoute.types'
import { DASH_NAV_V2_HEIGHT } from '../../legacy/common/theme'
import { ContactHeader } from '../../routes/utils/ContactHeader'
import { MenuBarTop } from '../../gin-and-tonic/containers/nav-v2/components/MenuBarTop'
import { useNavHierarchyAdmin } from '../../gin-and-tonic/containers/nav-v2/NavMenuHierarchy'
import { LeftShortcutsPaneAdmin } from '../../gin-and-tonic/containers/nav-v2/components/LeftShortcutsPane/LeftShortcutsPaneAdmin'
import {
  TdsNavigationBar,
  TOPBAR_HEIGHT,
  SIDEBAR_WIDTH,
  SIDEBAR_COLLAPSED_WIDTH,
} from '../../gin-and-tonic/containers/nav-v3/TdsNavigationBar'
import { TdsSearchButton } from '../../gin-and-tonic/containers/nav-v3/TdsSearchButton'
import { TdsPartnerMenu } from '../../gin-and-tonic/containers/nav-v3/TdsPartnerMenu'
import { NavVersionSwitcherButton } from '../../gin-and-tonic/containers/nav-v3/NavVersionSwitcherButton'
import { useNavHierarchyAdminV3 } from '../../gin-and-tonic/containers/nav-v3/useNavHierarchyAdminDataV3'
import { useV3SearchActionsAdmin } from '../../gin-and-tonic/containers/nav-v3/useV3SearchActions'
import { useSidebarCollapsedPersistence } from '../../gin-and-tonic/containers/nav-v3/useSidebarCollapsedPersistence'
import { useWindowSizeIsWide } from '../../utils/window/windows.hooks'
import { useNavContext } from '../../gin-and-tonic/containers/nav/utils/NavContext'
import { useDatadogPrivacy } from '../../gin-and-tonic/containers/datadog-privacy/DatadogPrivacy'
import { useAdminControls } from '../../utils/contexts/admin-controls/AdminControlsContext'
import { AiPageLayout } from '../../utils/contexts/ai/AiPageLayout'
import { useImpersonationBannerHeight } from '../../gin-and-tonic/containers/impersonation-status-banner/ImpersonationStatusBanner'
import { AdminSectionRoutes } from './AdminSectionRoutes'

interface Props extends DashRouteComponentProps {}

// Single source of truth for whether the admin UI renders the new TDS
// NavigationBar (v3) or the legacy MenuBarTop (v2). Backed by the same
// Admin Controls toggle used by partner-scoped, so flipping it switches both.
const useTdsNav = () => useAdminControls().useTdsNav

/**
 * v2 nav bar wrapper. Owns `useNavHierarchyAdmin()` so its context
 * subscriptions don't ripple into AdminSection.
 */
const AdminNavBarV2: FunctionComponent = () => {
  const { navMenuHierarchy } = useNavHierarchyAdmin()
  return <MenuBarTop navMenuHierarchy={navMenuHierarchy} />
}

/**
 * v3 nav bar wrapper. Owns `useNavHierarchyAdminV3()`, which assembles the
 * 8-L1 admin sidebar (Performance / Catalog / Marketplace / Customers &
 * orders / Connected stores / Fulfillment / Financial integrity / Partner
 * settings) from `domainAdminNavigationEntriesV3` — see useNavHierarchyAdminDataV3.tsx.
 */
const AdminNavBarV3: FunctionComponent<{
  isCollapsed: boolean
  setIsCollapsed: (next: boolean) => void
}> = ({ isCollapsed, setIsCollapsed }) => {
  const { navMenuHierarchy } = useNavHierarchyAdminV3()
  const searchActions = useV3SearchActionsAdmin()
  const theme = useTheme()
  // Hack: TDS NavigationBar exposes no color/theme prop. Force just the topbar
  // (<header>) dark to match v2 admin's `systemGrayscale80` styling — gives
  // users the visual cue they're in the admin area without restyling the
  // sidebar. The wrapper must propagate the parent flex column's vertical
  // space (flex: 1 + flexDirection: column); TDS Sidebar relies on flexGrow
  // inside NavigationBar's own Flex.Column, so a content-sized wrapper would
  // collapse the sidebar to zero height.
  return (
    <div
      css={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        '& header': {
          backgroundColor: `${theme.colors.systemGrayscale80} !important`,
        },
        // TDS NavigationBar.Button (via ids-core ButtonCore) keys hover/press
        // off `data-hovered`/`data-pressed` attributes; default backgrounds
        // are `brandPrimaryRegular`/`brandPrimaryExtraDark` (green). Swap to
        // grays so admin button feedback matches the dark topbar.
        '& header button[data-hovered="true"]': {
          backgroundColor: `${theme.colors.systemGrayscale60} !important`,
        },
        '& header button[data-pressed="true"]': {
          backgroundColor: `${theme.colors.systemGrayscale70} !important`,
        },
      }}
    >
      <TdsNavigationBar
        navMenuHierarchy={navMenuHierarchy}
        isCollapsed={isCollapsed}
        onCollapsedChange={setIsCollapsed}
        homeRoute="app-admin"
        topBarRight={
          <>
            <NavVersionSwitcherButton target="v2" appearance="admin" />
            <TdsSearchButton actions={searchActions} />
            <TdsPartnerMenu />
          </>
        }
      />
    </div>
  )
}

export const AdminSection: FunctionComponent<PropsWithChildren<Props>> = ({
  location: { pathname },
}) => {
  const { isNavVisible, isSideNavVisible } = useNavContext()
  const tdsNav = useTdsNav()

  const { isOpen, onOpen, onClose, onClick, feature } = useFeatureEducation(pathname)

  useEffect(() => {
    if (isOpen) {
      onOpen()
    }
  }, [isOpen, onOpen, pathname])

  const isWide = useWindowSizeIsWide()
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useSidebarCollapsedPersistence()
  const currentSidebarWidth = isSidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH
  const bannerHeight = useImpersonationBannerHeight()

  // Since admin has more privileged or sensitive tools, we mask user input by default
  useDatadogPrivacy('mask-user-input')

  if (tdsNav) {
    return (
      <div
        css={{
          display: 'flex',
          flexDirection: 'column',
          height: `calc(100vh - ${bannerHeight}px)`,
          width: '100vw',
          position: 'relative',
        }}
      >
        {isNavVisible && (
          <AdminNavBarV3 isCollapsed={isSidebarCollapsed} setIsCollapsed={setIsSidebarCollapsed} />
        )}
        <div
          css={{
            position: 'absolute',
            top: isNavVisible ? TOPBAR_HEIGHT : 0,
            left: isNavVisible ? currentSidebarWidth : 0,
            width: isNavVisible ? `calc(100% - ${currentSidebarWidth}px)` : '100%',
            height: isNavVisible ? `calc(100% - ${TOPBAR_HEIGHT}px)` : '100%',
            display: 'flex',
          }}
        >
          <FeatureEducationModal
            // @ts-expect-error TS2322 — strictNullChecks. TODO: fix strict null check
            isOpen={isOpen}
            onClose={onClose}
            onClick={onClick}
            // @ts-expect-error TS2322 — strictNullChecks. TODO: fix strict null check
            feature={feature}
          />
          <AiPageLayout>
            <AppContentContainer css={{ flex: 1, minWidth: 0, overflow: 'auto' }}>
              <ContactHeader />
              <ErrorBoundary pathname={pathname}>
                <AdminSectionRoutes />
              </ErrorBoundary>
            </AppContentContainer>
          </AiPageLayout>
        </div>
      </div>
    )
  }

  const topPadding = isNavVisible ? DASH_NAV_V2_HEIGHT : '0px'
  const leftPadding = '0px'

  return (
    <>
      {isNavVisible && <AdminNavBarV2 />}

      <div
        css={{
          display: 'flex',
          position: 'absolute',

          left: leftPadding,
          top: topPadding,

          width: `calc(100% - ${leftPadding})`,
          minHeight: `calc(100% - ${topPadding})`,
          height: `calc(100% - ${topPadding})`,
        }}
      >
        {isNavVisible && isSideNavVisible && isWide && <LeftShortcutsPaneAdmin />}

        <FeatureEducationModal
          // @ts-expect-error TS2322 — strictNullChecks. TODO: fix strict null check
          isOpen={isOpen}
          onClose={onClose}
          onClick={onClick}
          // @ts-expect-error TS2322 — strictNullChecks. TODO: fix strict null check
          feature={feature}
        />
        <AiPageLayout>
          <AppContentContainer>
            <ContactHeader />
            <ErrorBoundary pathname={pathname}>
              <AdminSectionRoutes />
            </ErrorBoundary>
          </AppContentContainer>
        </AiPageLayout>
      </div>
    </>
  )
}
